import React, { useContext, useState } from 'react';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import {
  IAddValueResult,
  useAddValueMutation,
  useDeleteValueMutation
} from '../../generated/graphql';
import { addValuesToCache, removeValueFromCache } from '../../utils/clientUtils';
import { ValueListNames } from '../../utils/constants';
import { ValueType } from './Customatization';
import DialogBox from '../Common/Layout/DialogBox';
import { Box, Button, Chip, SxProps } from '@mui/material';
import InputComponent from '../Common/Fields/InputComponent';
import { Colors } from '../../utils/theme';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';

type IProps = {
  name: string;
  values: ValueType[];
  addNew: boolean;
  handleClose: () => void;
};

type ValueListItem = ValueType & {
  isNew?: boolean;
};

const chipStyles: SxProps = {
  color: Colors.main,
  fontSize: 14,
  background: '#69AAF333',
  '&:active, &:focus': {
    background: '#69AAF333'
  }
};

const newChipStyles: SxProps = {
  color: Colors.skyBlue,
  background: Colors.white,
  fontSize: 14,
  border: '1px solid ' + Colors.skyBlue,
  '&:active, &:focus': {
    background: Colors.white
  }
};

const deleteIconStyles: SxProps = {
  color: Colors.skyBlue,
  width: 28,
  height: 28,
  '&:hover': {
    color: Colors.main
  }
};

const ValueList: React.FunctionComponent<IProps> = ({
  name,
  values,
  addNew,
  handleClose
}: IProps) => {
  const { dispatch } = useContext(AppContext);
  const [items, setItems] = useState<ValueListItem[]>(values);
  const [newItems, setNewItems] = useState<ValueListItem[]>([]);
  const [newValue, setNewValue] = useState('');
  const [addNewValue] = useAddValueMutation();
  const [deleteValueMutation] = useDeleteValueMutation();

  const handleAddValuePressed = () => {
    if (newValue.length > 0) {
      const canAddValue = canAddNewValue(newValue.trim());
      if (canAddValue) {
        const item = { id: newValue, name: newValue.trim(), isNew: true };
        setItems([item, ...items]);
        setNewItems([item, ...newItems]);
        setNewValue('');
      } else {
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            text: `Value ${newValue} already exists`,
            severity: 'warning',
            autoHide: 2000
          }
        });
      }
    }
  };

  const handleDeleteItemPressed = (item: ValueListItem) => {
    const { id, isNew } = item;
    if (isNew) {
      setItems(items.filter((i) => i.id !== id));
      setNewItems(newItems.filter((i) => i.id !== id));
    } else {
      dispatch({
        type: Actions.ShowConfirmation,
        payload: {
          text: `Are you sure you want to delete ${item.name} from ${name
            .replace(/([A-Z][a-z])/g, ' $1')
            .replace(/(\d)/g, ' $1')} value list?`,
          params: item,
          agreeAction: handleDeleteValueItem
        }
      });
    }
  };

  const handleDeleteValueItem = (valueItem: ValueListItem) => {
    dispatch({
      type: Actions.HideConfirmation
    });
    deleteValueMutation({
      variables: {
        value_list_name: ValueListNames[name],
        value_id: parseInt(valueItem?.id)
      },
      update() {
        setItems(items.filter((i) => i.id !== valueItem.id));
        removeValueFromCache(name as string, valueItem);
        dispatch({
          type: Actions.ShowMessage,
          payload: { text: `Value ${valueItem.name} deleted`, severity: 'success' }
        });
      }
    }).catch((e) => {
      const message = e.message.includes('update or delete on table')
        ? `Value ${valueItem.name} cannot be deleted!`
        : e.message;
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: message,
          autoHide: 6000
        }
      });
    });
  };

  const handleSavePressed = () => {
    if (newItems.length > 0) {
      const valuesArray = newItems.map((i) => i.name);
      addNewValue({
        variables: {
          input: {
            value_list_name: ValueListNames[name],
            new_values: valuesArray
          }
        },
        update(_, { data }) {
          setNewItems([]);
          const updatedValues = [] as ValueListItem[];
          if (data && data.result) {
            addValuesToCache(name, data?.result as IAddValueResult[]);
          }
          items.forEach((item) => {
            const index = Number(data?.result.findIndex((i) => i?.name === item.id));
            if (index > -1) {
              updatedValues.push({
                id: Number(data?.result[index]?.id).toString(),
                name: data?.result[index]?.name as string
              });
            } else {
              updatedValues.push(item);
            }
          });
          setItems(updatedValues);
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: 'Values saved successfully'
            }
          });
        }
      }).catch((e) =>
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        })
      );
    }
  };

  const hanldeCancelPressed = () => {
    if (newItems.length > 0) {
      dispatch({
        type: Actions.ShowConfirmation,
        payload: {
          text: 'There are unsaved values. Are you sure you want to proceed?',
          agreeAction: executeClose
        }
      });
    } else {
      executeClose();
    }
  };

  const executeClose = () => {
    dispatch({
      type: Actions.HideConfirmation
    });
    handleClose();
  };

  const handleKeyPressed = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddValuePressed();
    }
  };

  const canAddNewValue = (value: string): boolean => {
    let result = true;
    result = newItems.filter((i) => i.name.toUpperCase() === value.toUpperCase()).length === 0;
    if (result) {
      result = items.filter((i) => i.name.toUpperCase() === value.toUpperCase()).length === 0;
    }
    return result;
  };

  return (
    <DialogBox
      id="new-user-dialog"
      width={886}
      title={name.replace(/([A-Z][a-z])/g, ' $1').replace(/(\d)/g, ' $1') + ' Value List'}
      onClose={handleClose}
    >
      <Box
        sx={{
          height: '618px',
          display: 'flex',
          flexFlow: 'column',
          paddingY: '50px',
          gap: '50px',
          overflow: 'auto'
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
            gap: '20px 24px',
            paddingLeft: '13%',
            paddingRight: '13%',
            flexGrow: 1,
            gridTemplateRows: 'max-content'
          }}
        >
          {addNew && (
            <>
              <InputComponent
                id="new-value"
                data-testid="new-value-input-label"
                label="New Value"
                value={newValue}
                onChange={(e) => setNewValue(e.currentTarget.value)}
                onKeyPress={handleKeyPressed}
                additionalStyle={{
                  gridColumn: 'span 8'
                }}
              />
              <Button
                id="add-new-value-button"
                data-testid="add-new-value-button"
                sx={{
                  width: '126px',
                  height: '36px',
                  border: '1px solid ' + Colors.secondary,
                  color: Colors.secondary,
                  gridColumn: 'span 3'
                }}
                onClick={handleAddValuePressed}
              >
                Add Value
              </Button>
            </>
          )}
          <Box
            sx={{
              gridColumn: 'span 11',
              display: 'flex',
              gap: '20px',
              marginTop: '50px',
              flexWrap: 'wrap'
            }}
          >
            {items.map((val, index) => (
              <Box key={index}>
                <Chip
                  id={`${name}-${val.name.split(' ').join('-')}-val`}
                  data-testid={`${val.name.split(' ').join('-')}-val`}
                  label={val.name}
                  onDelete={() => handleDeleteItemPressed(val)}
                  sx={{
                    ...(val.isNew ? newChipStyles : chipStyles),
                    '.MuiChip-deleteIcon': deleteIconStyles
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box className="centralize" sx={{ gridColumn: 'span 12', margin: '15px' }}>
        <CancelButton
          id="cancel-new-value-button"
          data-testid="cancel-new-value-button"
          onClick={hanldeCancelPressed}
          text={addNew ? 'Cancel' : 'Close'}
        />
        {addNew && (
          <SaveButton
            id="create-new-value-button"
            data-testid="create-new-value-button"
            onClick={handleSavePressed}
            text={'Save'}
          />
        )}
      </Box>
    </DialogBox>
  );
};

export default ValueList;
